<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import ProductCard from '../components/ProductCard.vue'
import SocialShare from '../components/SocialShare.vue'

const route = useRoute()
const project = ref(null)
const loading = ref(true)

// For recommendations
const recommendedProducts = ref([])
const recommendedArticles = ref([])

// Utility to transform oembed to iframe
const transformOembed = (html) => {
  if (!html) return html;
  
  const buildYouTubeEmbed = (videoId) => {
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div></div>`;
  };

  const extractYouTubeId = (url) => {
    if (url.startsWith('www.')) url = 'https://' + url;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
    return ytMatch ? ytMatch[1] : null;
  };

  // 1. Transform <figure class="media"><oembed>...</oembed></figure> (CKEditor full structure)
  let result = html.replace(/<figure\s+class="media">\s*<oembed\s+url="([^"]+)">\s*<\/oembed>\s*<\/figure>/gi, (match, url) => {
    const videoId = extractYouTubeId(url);
    if (videoId) return buildYouTubeEmbed(videoId);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${url}" frameborder="0" allowfullscreen></iframe></div></div>`;
  });

  // 2. Transform standalone <oembed> tags (without figure wrapper)
  result = result.replace(/<oembed\s+url="([^"]+)"><\/oembed>/gi, (match, url) => {
    const videoId = extractYouTubeId(url);
    if (videoId) return buildYouTubeEmbed(videoId);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${url}" frameborder="0" allowfullscreen></iframe></div></div>`;
  });

  // 3. Transform <figure class="media"> wrapping an iframe
  result = result.replace(/<figure\s+class="media">\s*(<iframe[^>]*?>[\s\S]*?<\/iframe>)\s*<\/figure>/gi, (match, iframeTag) => {
    const ytMatch = iframeTag.match(/src="https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})([^"]*)"/i);
    if (ytMatch) return buildYouTubeEmbed(ytMatch[1]);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner">${iframeTag}</div></div>`;
  });

  // 4. Fix any remaining standalone youtube iframes not inside figure.media
  result = result.replace(/<iframe([^>]*?)src="https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})([^"]*)"([^>]*?)>(\s*<\/iframe>)?/gi, (match, before, videoId, params, after) => {
    return buildYouTubeEmbed(videoId);
  });

  return result;
};

const fetchProjectDetail = async () => {
    try {
        const slug = route.params.slug
        const res = await fetch(`/api/projects/${slug}`)
        const data = await res.json()
        if (data.success) {
            project.value = data.data
            if (project.value.content_rich) {
                project.value.content_rich = transformOembed(project.value.content_rich)
            } else if (project.value.description) {
                project.value.description = transformOembed(project.value.description)
            }
        }
    } catch (error) {
        console.error('Fetch error:', error)
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchProjectDetail()
    fetchRecommended()
    window.addEventListener('keydown', handleKeydown)
    
    // Setup scroll observer for animations
    setupScrollObserver()
})

const fetchRecommended = async () => {
    try {
        // Fetch products
        const prodRes = await fetch('/api/products?limit=4&is_active=true')
        const prodData = await prodRes.json()
        if (prodData.success) {
            // Filter out the current project's product if it's the same
            const excludeId = project.value?.product_id || null
            recommendedProducts.value = prodData.data.filter(p => p.id !== excludeId).slice(0, 4)
        }

        // Fetch articles
        const artRes = await fetch('/api/articles?limit=3&status=published')
        const artData = await artRes.json()
        if (artData.success) {
            recommendedArticles.value = artData.data
        }
    } catch (e) {
        console.error('Failed to fetch recommendations', e)
    }
}

// Scroll Animation Observer
const setupScrollObserver = () => {
    setTimeout(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in-up')
                    entry.target.classList.remove('opacity-0', 'translate-y-8')
                    observer.unobserve(entry.target)
                }
            })
        }, { threshold: 0.1 })

        document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
            observer.observe(el)
        })
    }, 500)
}

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
})

const getImageUrl = (path) => {
    if (!path) return ''
    if (path.startsWith('http')) return path
    return `${import.meta.env.VITE_API_URL || ''}${path}`
}

const getProductImage = (prod) => {
    if (!prod) return '';
    if (prod.image_url) return getImageUrl(prod.image_url);
    if (prod.images) {
        try {
            const parsed = typeof prod.images === 'string' ? JSON.parse(prod.images) : prod.images;
            if (Array.isArray(parsed) && parsed.length > 0) {
                return getImageUrl(parsed[0]);
            }
        } catch (e) {
            console.error('Failed to parse product images', e);
        }
    }
    return '';
}

// Lightbox state and functions
const isLightboxOpen = ref(false)
const currentImageIndex = ref(0)

const openLightbox = (index) => {
    currentImageIndex.value = index
    isLightboxOpen.value = true
    document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
    isLightboxOpen.value = false
    document.body.style.overflow = ''
}

const nextImage = () => {
    if (project.value && project.value.gallery_images) {
        currentImageIndex.value = (currentImageIndex.value + 1) % project.value.gallery_images.length
    }
}

const prevImage = () => {
    if (project.value && project.value.gallery_images) {
        currentImageIndex.value = (currentImageIndex.value - 1 + project.value.gallery_images.length) % project.value.gallery_images.length
    }
}

const handleKeydown = (e) => {
    if (!isLightboxOpen.value) return
    if (e.key === 'Escape') closeLightbox()
    if (e.key === 'ArrowRight') nextImage()
    if (e.key === 'ArrowLeft') prevImage()
}

const formatPrice = (price) => {
    if (!price) return '0'
    return parseFloat(price).toLocaleString('th-TH')
}

const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })
}

</script>

<template>
<div class="bg-slate-50 dark:bg-[#080c14] min-h-screen pb-24">
    <!-- Loading state -->
    <div v-if="loading" class="min-h-[70vh] flex flex-col items-center justify-center">
        <div class="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p class="text-slate-500 dark:text-slate-400 font-bold text-sm tracking-wide">กำลังโหลดข้อมูลผลงานสุดพิเศษ...</p>
    </div>
    
    <!-- Loaded state -->
    <div v-else-if="project" class="relative">
        <!-- Premium Full-width Hero Header -->
        <div class="relative h-[340px] sm:h-[440px] md:h-[520px] w-full overflow-hidden bg-slate-900 select-none">
            <!-- Hero Image with subtle zoom on load -->
            <img :src="getImageUrl(project.cover_image)" :alt="project.title" class="w-full h-full object-cover scale-[1.01] hover:scale-105 transition-transform duration-[4000ms] ease-out">
            
            <!-- Hero Gradient Overlay (Dark bottom for clear title text contrast) -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20"></div>
            
            <!-- Floating Navigation / Breadcrumbs -->
            <div class="absolute top-28 left-0 right-0 z-20">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <router-link to="/projects" class="inline-flex items-center gap-2 px-4.5 py-2.5 rounded-full bg-white/10 dark:bg-black/40 hover:bg-emerald-600 hover:text-white backdrop-blur-md text-xs font-black text-white uppercase tracking-wider transition-all duration-300 border border-white/10 group shadow-lg">
                        <svg class="w-4 h-4 transform group-hover:-translate-x-1.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                        </svg>
                        กลับไปหน้ารวมผลงาน
                    </router-link>
                </div>
            </div>
            
            <!-- Hero Text Content -->
            <div class="absolute inset-x-0 bottom-0 pb-20 sm:pb-24 z-10">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="max-w-4xl">
                        <!-- Tiny Tag badge -->
                        <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500 text-white rounded-md text-[10px] font-black uppercase tracking-wider mb-4 shadow-sm select-none">
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
                            PROJECT SHOWCASE
                        </div>
                        
                        <!-- Main Title -->
                        <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-[3.85rem] font-black text-white tracking-tight leading-[1.1] drop-shadow-md">
                            {{ project.title }}
                        </h1>
                    </div>
                </div>
            </div>
        </div>

        <!-- Floating Glassmorphic Details Bar (Overlaps bottom of Hero) -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 sm:-mt-14 relative z-30 mb-10">
            <div class="bg-white/80 dark:bg-[#0f172a]/80 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-wrap items-center justify-between gap-6 md:gap-4">
                <!-- Location Info -->
                <div class="flex items-center gap-3.5 min-w-[200px] flex-1">
                    <div class="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 border border-emerald-500/10 shadow-sm shrink-0">
                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <div>
                        <span class="block text-[10px] font-black text-slate-450 dark:text-slate-400 uppercase tracking-wider">สถานที่ติดตั้ง</span>
                        <span class="text-base font-black text-slate-900 dark:text-white mt-0.5 block leading-tight">{{ project.location || 'สถานที่: ไม่ระบุ' }}</span>
                    </div>
                </div>

                <!-- Date Info -->
                <div v-if="project.service_date" class="flex items-center gap-3.5 min-w-[200px] flex-1">
                    <div class="w-12 h-12 rounded-2xl bg-teal-50 dark:bg-teal-500/10 flex items-center justify-center text-teal-600 dark:text-teal-400 border border-teal-500/10 shadow-sm shrink-0">
                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <div>
                        <span class="block text-[10px] font-black text-slate-450 dark:text-slate-400 uppercase tracking-wider">วันที่แล้วเสร็จ</span>
                        <span class="text-base font-black text-slate-900 dark:text-white mt-0.5 block leading-tight">{{ formatDate(project.service_date) }}</span>
                    </div>
                </div>

                <!-- Client Info -->
                <div v-if="project.client_name" class="flex items-center gap-3.5 min-w-[200px] flex-1">
                    <div class="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center text-amber-600 dark:text-amber-400 border border-amber-500/10 shadow-sm shrink-0">
                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <div>
                        <span class="block text-[10px] font-black text-slate-450 dark:text-slate-400 uppercase tracking-wider">ลูกค้า</span>
                        <span class="text-base font-black text-slate-900 dark:text-white mt-0.5 block leading-tight">{{ project.client_name }}</span>
                    </div>
                </div>

                <!-- Reference Product Link -->
                <div v-if="project.product" class="flex items-center gap-3.5 min-w-[200px] flex-1">
                    <div class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400 border border-indigo-500/10 shadow-sm shrink-0">
                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                    </div>
                    <div>
                        <span class="block text-[10px] font-black text-slate-450 dark:text-slate-400 uppercase tracking-wider">รุ่นสินค้าที่ติดตั้ง</span>
                        <router-link :to="'/products/' + (project.product.slug || project.product.id)" class="text-base font-black text-emerald-600 dark:text-emerald-400 hover:underline mt-0.5 block leading-tight truncate max-w-[170px]">
                            {{ project.product.name }}
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <!-- Layout Container -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
                
                <!-- Main Content Column -->
                <div class="lg:col-span-8 flex flex-col gap-8">
                    <!-- Main Card content description -->
                    <div class="bg-white dark:bg-[#0f172a] rounded-[2.5rem] p-6 sm:p-10 md:p-12 shadow-sm border border-slate-100 dark:border-white/5">
                        
                        <!-- Linked Product Highlight Box -->
                        <div v-if="project.product" class="mb-10">
                            <div class="bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-[#131d2e] dark:to-[#1a263b] rounded-3xl p-6 border border-slate-200/50 dark:border-slate-800/80 shadow-inner relative overflow-hidden group">
                                <!-- Background decoration element -->
                                <div class="absolute -right-24 -bottom-24 w-60 h-60 bg-emerald-500/5 dark:bg-emerald-500/10 rounded-full blur-3xl pointer-events-none group-hover:scale-125 transition-transform duration-700"></div>
                                
                                <div class="flex flex-col md:flex-row gap-6 items-center relative z-10">
                                    <div class="w-36 h-36 flex-shrink-0 bg-white dark:bg-[#0f172a] rounded-2xl overflow-hidden shadow-sm border border-slate-200/60 dark:border-slate-700/50 p-2 flex items-center justify-center relative">
                                        <img :src="getProductImage(project.product)" :alt="project.product.name" class="w-full h-full object-contain mix-blend-multiply dark:mix-blend-normal group-hover:scale-105 transition-transform duration-500">
                                        <!-- Discount tag -->
                                        <div v-if="project.product.original_price && project.product.original_price > project.product.price" class="absolute top-2 left-2 bg-rose-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow">
                                            ลด {{ Math.round((1 - project.product.price / project.product.original_price) * 100) }}%
                                        </div>
                                    </div>
                                    <div class="flex-1 text-center md:text-left">
                                        <span class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-450 text-[10px] font-black rounded-full uppercase tracking-wider mb-2">
                                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
                                            สินค้าที่ใช้ในผลงานนี้
                                        </span>
                                        <h3 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mb-2 leading-tight">{{ project.product.name }}</h3>
                                        <p v-if="project.product.short_description" class="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed mb-4 max-w-xl">
                                            {{ project.product.short_description }}
                                        </p>
                                        
                                        <div class="flex flex-wrap items-center justify-center md:justify-between gap-4 border-t border-slate-200/50 dark:border-slate-800/80 pt-4 mt-2">
                                            <div v-if="project.product.price && project.product.price > 0" class="flex items-baseline gap-2">
                                                <span class="text-xl font-black text-red-600 dark:text-red-500">฿{{ formatPrice(project.product.price) }}</span>
                                                <span v-if="project.product.original_price && project.product.original_price > project.product.price" class="text-xs text-slate-400 line-through">฿{{ formatPrice(project.product.original_price) }}</span>
                                            </div>
                                            <div v-else class="text-sm font-black text-slate-450">สอบถามราคาการติดตั้ง</div>
                                            
                                            <router-link :to="'/products/' + (project.product.slug || project.product.id)" class="inline-flex items-center gap-1.5 px-4.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl shadow-md shadow-emerald-600/10 transition-all active:scale-95">
                                                ดูข้อมูลรายละเอียดสินค้า
                                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                                            </router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Rich Content Details (CKEditor customized container) -->
                        <div class="prose prose-slate dark:prose-invert max-w-none ck-content text-slate-700 dark:text-slate-350" v-html="project.content_rich || project.description"></div>
                    </div>

                    <!-- Premium Mosaic Gallery Grid -->
                    <div v-if="project.gallery_images && project.gallery_images.length > 0" class="bg-white dark:bg-[#0f172a] rounded-[2.5rem] p-6 sm:p-10 md:p-12 shadow-sm border border-slate-100 dark:border-white/5">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                            <div>
                                <h3 class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">ภาพบรรยากาศการติดตั้งเพิ่มเติม</h3>
                                <p class="text-slate-500 dark:text-slate-400 mt-1.5 text-sm font-medium">ภาพถ่ายถ่ายทอดผลงานจริงทุกมุมมอง ณ หน้างานลูกค้าจริง</p>
                            </div>
                            <div class="inline-flex items-center gap-2 text-xs font-extrabold text-emerald-600 dark:text-emerald-450 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5 rounded-full border border-emerald-500/10 shrink-0 self-start sm:self-auto select-none">
                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                รวมภาพติดตั้ง {{ project.gallery_images.length }} รูป
                            </div>
                        </div>

                        <!-- Modern Asymmetric Mosaic Grid with Hover Animations -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 auto-rows-[140px] sm:auto-rows-[180px] md:auto-rows-[220px]">
                            <div 
                                v-for="(img, idx) in project.gallery_images" 
                                :key="idx" 
                                @click="openLightbox(idx)" 
                                :class="[
                                    'relative group rounded-2xl overflow-hidden cursor-pointer border border-slate-100 dark:border-white/5 transition-all duration-500 hover:shadow-xl hover:-translate-y-1',
                                    idx === 0 && project.gallery_images.length > 2 ? 'col-span-2 row-span-2' : '',
                                    idx === 1 && project.gallery_images.length > 3 ? 'col-span-2 row-span-1' : '',
                                    idx === 2 && project.gallery_images.length > 3 ? 'col-span-1 row-span-1' : '',
                                    idx === 3 && project.gallery_images.length > 4 ? 'col-span-1 row-span-1' : ''
                                ]"
                            >
                                <img :src="getImageUrl(img)" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">
                                
                                <!-- Glassmorphic hover overlay with magnifying icon -->
                                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                                    <div class="bg-white/10 text-white rounded-full p-4 transform translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-md shadow-lg border border-white/20">
                                        <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
                                    </div>
                                    <div class="absolute bottom-4 left-4 text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        คลิกเพื่อขยายดูรูป
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sticky Sidebar Marketing Hub -->
                <div class="lg:col-span-4 lg:sticky lg:top-28 flex flex-col gap-6">
                    <div class="bg-white dark:bg-[#0f172a] rounded-[2.5rem] p-6 sm:p-8 shadow-sm border border-slate-100 dark:border-white/5 flex flex-col gap-6">
                        
                        <!-- Contact Action Header -->
                        <div>
                            <h3 class="text-base font-black text-slate-900 dark:text-white uppercase tracking-wider mb-4 border-b border-slate-100 dark:border-white/5 pb-3">ปรึกษางานติดตั้งฟรี</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">มีเจ้าหน้าที่คอยช่วยเหลือ ประเมิน และ แนะนำรุ่นที่ตอบโจทย์ขนาดหน้างานทุกวัน</p>
                        </div>

                        <!-- CTA Controls -->
                        <div class="flex flex-col gap-3">
                            <!-- Link to Quotation -->
                            <router-link 
                                :to="project.product ? `/quotation?product_id=${project.product.id}` : '/quotation'"
                                class="w-full py-4 text-center bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white text-sm font-extrabold rounded-2xl shadow-lg shadow-emerald-600/15 active:scale-98 transition-all flex items-center justify-center gap-2"
                            >
                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                ขอใบเสนอราคารุ่นติดตั้งนี้
                            </router-link>

                            <!-- Line chat -->
                            <a 
                                href="https://line.me/ti/p/@greenhouse" 
                                target="_blank"
                                class="w-full py-4 text-center bg-[#06C755] hover:bg-[#05b04b] text-white text-sm font-extrabold rounded-2xl shadow-md shadow-green-500/10 active:scale-98 transition-all flex items-center justify-center gap-2 cursor-pointer"
                            >
                                <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M24 10.3c0-5.7-5.4-10.3-12-10.3S0 4.6 0 10.3c0 5.1 4.3 9.3 10.1 10.1.4.1.9.3 1 .6l.4 1.7c.1.5-.1.8-.3 1-.2.2-.5.1-.8-.1l-4-3.7c-.2-.1-.3-.2-.5-.2-3.6-.3-5.9-2.9-5.9-5.4 0-5.1 4.8-9.3 10.7-9.3s10.7 4.2 10.7 9.3c0 5.1-4.8 9.3-10.7 9.3h-.5c-.3 0-.6.2-.7.5-.1.3-.1.6.1.8l1.5 1.4c.2.2.3.5.2.8s-.4.4-.7.4c-4.4 0-8.2-2.7-8.2-6.5"/></svg>
                                สอบถามผ่าน LINE Official
                            </a>
                            
                            <!-- Onsite survey booking -->
                            <router-link 
                                to="/contact?purpose=onsite"
                                class="w-full py-4 text-center bg-white hover:bg-slate-50 dark:bg-[#111827] dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-extrabold rounded-2xl border border-slate-250 dark:border-white/10 active:scale-98 transition-all flex items-center justify-center gap-2"
                            >
                                <svg class="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/></svg>
                                จองคิววัดพื้นที่หน้างานฟรี
                            </router-link>
                        </div>

                        <!-- Sidebar reference product snippet -->
                        <div v-if="project.product" class="border-t border-slate-100 dark:border-white/5 pt-5">
                            <h4 class="text-xs font-black text-slate-400 uppercase tracking-wider mb-3.5">รุ่นสินค้าติดตั้ง</h4>
                            <div class="flex items-center gap-3.5 bg-slate-50 dark:bg-slate-800/40 p-3 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div class="w-12 h-12 bg-white rounded-lg p-0.5 flex-shrink-0 flex items-center justify-center shadow-inner">
                                    <img :src="getProductImage(project.product)" class="w-full h-full object-contain mix-blend-multiply" />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <span class="block text-xs font-bold text-slate-900 dark:text-white truncate">{{ project.product.name }}</span>
                                    <span v-if="project.product.sku" class="block text-[10px] text-slate-450 dark:text-slate-500 font-mono mt-0.5">{{ project.product.sku }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Share options -->
                        <div class="border-t border-slate-100 dark:border-white/5 pt-5">
                            <span class="block text-center text-[10px] font-black text-slate-450 dark:text-slate-500 uppercase tracking-wider mb-3.5">แชร์ผลงานเพื่อเป็นไอเดีย</span>
                            <SocialShare />
                        </div>
                    </div>
                </div>

            </div>

            <!-- Recommended Products Section -->
            <div v-if="recommendedProducts.length > 0" class="mt-24 pt-16 border-t border-slate-200/50 dark:border-white/5">
                <div class="flex items-end justify-between mb-8">
                    <div>
                        <h3 class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">สินค้าแนะนำเพิ่มเติม</h3>
                        <p class="text-slate-500 dark:text-slate-400 mt-1 text-sm font-medium">โรงเก็บของและโรงเรือนสำเร็จรูปสเปคใกล้เคียงกัน</p>
                    </div>
                    <router-link to="/products" class="hidden sm:inline-flex items-center text-emerald-600 dark:text-emerald-450 font-black hover:text-emerald-700 dark:hover:text-emerald-350 hover:underline text-sm gap-1 group/link">
                        ดูสินค้าทั้งหมด
                        <svg class="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" /></svg>
                    </router-link>
                </div>
                
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                    <ProductCard 
                        v-for="prod in recommendedProducts" 
                        :key="prod.id"
                        :product="prod"
                    />
                </div>
            </div>

            <!-- Recommended Articles Section -->
            <div v-if="recommendedArticles.length > 0" class="mt-24 pt-16 border-t border-slate-200/50 dark:border-white/5">
                <div class="flex items-end justify-between mb-8">
                    <div>
                        <h3 class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">ไอเดียการจัดสวนและบทความน่ารู้</h3>
                        <p class="text-slate-500 dark:text-slate-400 mt-1 text-sm font-medium">คู่มือการเลือกบ้านเก็บของ การเตรียมหน้างาน และการดูแลรักษา</p>
                    </div>
                    <router-link to="/blog" class="hidden sm:inline-flex items-center text-emerald-600 dark:text-emerald-450 font-black hover:text-emerald-700 dark:hover:text-emerald-350 hover:underline text-sm gap-1 group/link2">
                        ดูบทความทั้งหมด
                        <svg class="w-4 h-4 transform group-hover/link2:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" /></svg>
                    </router-link>
                </div>
                
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    <router-link 
                        v-for="article in recommendedArticles" 
                        :key="article.id"
                        :to="article.slug ? `/blog/${article.slug}` : `/blog/${article.id}`"
                        class="group flex flex-col bg-white dark:bg-[#0f172a] rounded-[2rem] overflow-hidden border border-slate-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5"
                    >
                        <div class="h-48 relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                            <img v-if="article.cover_image" :src="getImageUrl(article.cover_image)" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">
                            <div v-else class="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500">
                                <svg class="w-12 h-12 opacity-20" fill="currentColor" viewBox="0 0 24 24"><path d="M4 4h16v16H4z" opacity=".2"/><path d="M4 2h16c1.1 0 2 .9 2 2v16c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2zm0 18h16V4H4v16zm11-4.5l-3.5 4.5-2.5-3L5 18h14l-4-4.5z"/></svg>
                            </div>
                            <div v-if="article.category" class="absolute top-3 left-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm text-emerald-700 dark:text-emerald-400 text-[9px] font-black px-2.5 py-1 rounded-md shadow-sm border border-emerald-500/10 uppercase tracking-wider">
                                {{ article.category }}
                            </div>
                        </div>
                        <div class="p-6 flex-1 flex flex-col">
                            <div class="text-[10px] text-slate-400 dark:text-slate-500 font-black mb-2 flex items-center gap-2">
                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {{ formatDate(article.published_at || article.created_at) }}
                            </div>
                            <h4 class="font-extrabold text-slate-950 dark:text-white leading-snug mb-3 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2">{{ article.title }}</h4>
                            <p class="text-xs text-slate-550 dark:text-slate-400 line-clamp-2 mt-auto leading-relaxed">{{ article.excerpt }}</p>
                        </div>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
    
    <!-- Fallback Not Found state -->
    <div v-else class="min-h-[70vh] flex flex-col items-center justify-center px-4 select-none">
        <div class="w-16 h-16 bg-rose-50 dark:bg-rose-500/10 rounded-full flex items-center justify-center text-rose-505 mb-4 border border-rose-500/15">
            <svg class="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
        </div>
        <h2 class="text-xl font-black text-slate-900 dark:text-white">ไม่พบข้อมูลผลงานที่ต้องการค้นหา</h2>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">ลิงก์อาจจะไม่ถูกต้อง หรือผลงานนี้ถูกนำออกจากระบบหลักแล้ว</p>
        <router-link to="/projects" class="mt-6 inline-flex items-center gap-1.5 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black rounded-xl shadow-md shadow-emerald-600/10 transition-all duration-300">
            กลับไปยังหน้ารวมผลงาน
        </router-link>
    </div>

    <!-- Lightbox Modal with Thumbnails and Slide Transitions -->
    <teleport to="body">
        <div v-if="isLightboxOpen" class="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/98 backdrop-blur-sm" @click="closeLightbox">
            <!-- Top Header Action Bar -->
            <div class="absolute top-0 inset-x-0 h-16 flex items-center justify-between px-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-50">
                <div class="text-white/80 font-mono text-xs font-bold pointer-events-auto">
                    {{ currentImageIndex + 1 }} / {{ project.gallery_images.length }}
                </div>
                <button @click.stop="closeLightbox" class="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors pointer-events-auto focus:outline-none">
                    <svg class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
            </div>

            <!-- Main Swipeable Image Area -->
            <div class="relative w-full flex-1 flex items-center justify-center px-4" @click.stop>
                <!-- Desktop Prev Image button -->
                <button @click="prevImage" class="absolute left-6 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all focus:outline-none hidden md:block">
                    <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"></path></svg>
                </button>

                <!-- Current Image Container with fade transition -->
                <div class="relative max-w-5xl max-h-[70vh] flex items-center justify-center overflow-hidden">
                    <transition name="fade" mode="out-in">
                        <img :key="currentImageIndex" :src="getImageUrl(project.gallery_images[currentImageIndex])" class="max-w-full max-h-[70vh] object-contain rounded-xl shadow-2xl select-none" />
                    </transition>
                </div>

                <!-- Desktop Next Image button -->
                <button @click="nextImage" class="absolute right-6 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all focus:outline-none hidden md:block">
                    <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path></svg>
                </button>
                
                <!-- Mobile Navigation Overlays -->
                <div class="absolute inset-y-0 left-0 w-1/3 z-40 md:hidden" @click="prevImage"></div>
                <div class="absolute inset-y-0 right-0 w-1/3 z-40 md:hidden" @click="nextImage"></div>
            </div>

            <!-- Bottom Thumbnails Carousel -->
            <div class="w-full max-w-4xl px-6 py-6 bg-gradient-to-t from-black/60 to-transparent flex flex-col gap-3 items-center z-50" @click.stop>
                <div class="flex gap-2 justify-center overflow-x-auto py-1 max-w-full no-scrollbar">
                    <div 
                        v-for="(img, idx) in project.gallery_images" 
                        :key="idx"
                        @click="currentImageIndex = idx"
                        :class="[
                            'w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 shrink-0 select-none',
                            currentImageIndex === idx ? 'border-emerald-500 scale-105 shadow-md shadow-emerald-500/20' : 'border-white/10 opacity-50 hover:opacity-100'
                        ]"
                    >
                        <img :src="getImageUrl(img)" class="w-full h-full object-cover" />
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</div>
</template>

<style scoped>
/* ══════════════════════════════════════════════════
   CKEditor Content Styling - ProjectDetail Redesign
   ══════════════════════════════════════════════════ */

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(2rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Slide Transition for Lightbox */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1), transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.fade-enter-from {
  opacity: 0;
  transform: scale(0.97);
}
.fade-leave-to {
  opacity: 0;
  transform: scale(1.03);
}

/* ── Headings ── */
.ck-content :deep(h2) {
  font-size: 1.85rem;
  font-weight: 900;
  color: #0f172a;
  margin-top: 3rem;
  margin-bottom: 1.25rem;
  line-height: 1.3;
  letter-spacing: -0.02em;
  border-left: 5px solid #10b981;
  padding-left: 1.25rem;
}

.ck-content :deep(h3) {
  font-size: 1.45rem;
  font-weight: 850;
  color: #0f172a;
  margin-top: 2.25rem;
  margin-bottom: 0.85rem;
  line-height: 1.4;
}

.ck-content :deep(h4) {
  font-size: 1.2rem;
  font-weight: 800;
  color: #1e293b;
  margin-top: 1.75rem;
  margin-bottom: 0.6rem;
}

:global(.dark) .ck-content :deep(h2) {
  color: #f8fafc;
  border-left-color: #34d399;
}
:global(.dark) .ck-content :deep(h3) { color: #f8fafc; }
:global(.dark) .ck-content :deep(h4) { color: #cbd5e1; }

/* ── Paragraphs ── */
.ck-content :deep(p) {
  color: #334155;
  line-height: 1.95;
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
  font-weight: 400;
}

:global(.dark) .ck-content :deep(p) {
  color: #cbd5e1;
}

/* ── Strong / Bold ── */
.ck-content :deep(strong) {
  color: #0f172a;
  font-weight: 900;
}

:global(.dark) .ck-content :deep(strong) {
  color: #ffffff;
}

/* ── Lists ── */
.ck-content :deep(ul),
.ck-content :deep(ol) {
  padding-left: 1.75rem;
  margin-bottom: 1.75rem;
}

.ck-content :deep(ul) { list-style-type: disc; }
.ck-content :deep(ol) { list-style-type: decimal; }

.ck-content :deep(li) {
  margin-bottom: 0.75rem;
  line-height: 1.85;
  color: #334155;
  font-size: 1.025rem;
  padding-left: 0.25rem;
}

:global(.dark) .ck-content :deep(li) { color: #cbd5e1; }

.ck-content :deep(li::marker) {
  color: #10b981;
  font-weight: 900;
}

/* ── Links ── */
.ck-content :deep(a) {
  color: #059669;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-color: #a7f3d0;
  transition: all 0.25s;
  font-weight: 700;
}

.ck-content :deep(a:hover) {
  color: #047857;
  text-decoration-color: #059669;
}

:global(.dark) .ck-content :deep(a) {
  color: #34d399;
  text-decoration-color: #065f46;
}

/* ── Blockquote ── */
.ck-content :deep(blockquote) {
  border-left: 5px solid #10b981;
  background-color: #f8fafc;
  padding: 1.5rem 1.75rem;
  margin: 2.5rem 0;
  border-radius: 0 16px 16px 0;
  font-style: italic;
  color: #334155;
  line-height: 1.8;
}

:global(.dark) .ck-content :deep(blockquote) {
  background-color: #111827;
  border-left-color: #34d399;
  color: #cbd5e1;
}

/* ── Media (Video/YouTube iframe) ── */
.ck-content :deep(.yt-embed-wrapper) {
  max-width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  margin: 2rem 0;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}
.ck-content :deep(.yt-embed-wrapper:hover) {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}
.ck-content :deep(.yt-embed-inner) {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  overflow: hidden;
}
.ck-content :deep(.yt-embed-inner iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ── CKEditor Images ── */
.ck-content :deep(figure.image) {
  margin: 2.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ck-content :deep(figure.image img) {
  max-width: 100%;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border: 1px solid #f1f5f9;
}

:global(.dark) .ck-content :deep(figure.image img) {
  border-color: #1e293b;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
}

.ck-content :deep(figure.image figcaption) {
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.85rem;
  font-style: italic;
  font-weight: 500;
}

:global(.dark) .ck-content :deep(figure.image figcaption) {
  color: #94a3b8;
}

/* ── Horizontal Rule ── */
.ck-content :deep(hr) {
  border: none;
  height: 2px;
  background: linear-gradient(90deg, transparent, #34d399, transparent);
  margin: 3rem 0;
}

:global(.dark) .ck-content :deep(hr) {
  background: linear-gradient(90deg, transparent, #065f46, transparent);
}

/* ── Tables ── */
.ck-content :deep(figure.table) {
  display: flex;
  justify-content: center;
  margin: 2.5rem auto;
  width: 100%;
}

.ck-content :deep(table) {
  width: 100% !important;
  table-layout: auto;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0 !important;
  margin: 2rem auto;
  font-size: 0.975rem;
}

.ck-content :deep(table thead) {
  background: linear-gradient(135deg, #059669, #10b981);
}

.ck-content :deep(table thead th) {
  color: white;
  font-weight: 800;
  padding: 16px 22px;
  text-align: left;
  border-bottom: 2px solid #047857;
}

.ck-content :deep(table tbody tr) {
  transition: background-color 0.2s ease;
}

.ck-content :deep(table tbody tr:nth-child(even)) {
  background-color: #f8fafc;
}

.ck-content :deep(table tbody tr:hover) {
  background-color: #f1f5f9;
}

.ck-content :deep(table tbody td) {
  padding: 14px 22px;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  line-height: 1.65;
}

.ck-content :deep(table tbody td:first-child) {
  font-weight: 700;
  color: #065f46;
}

.ck-content :deep(table tbody tr:last-child td) {
  border-bottom: none;
}

/* Dark Mode Tables */
:global(.dark) .ck-content :deep(table) {
  border-color: #1e293b !important;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  background-color: #0f172a;
}
:global(.dark) .ck-content :deep(table thead) {
  background: linear-gradient(135deg, #047857, #059669);
}
:global(.dark) .ck-content :deep(table tbody tr:nth-child(even)) {
  background-color: #1e293b/30;
}
:global(.dark) .ck-content :deep(table tbody tr:hover) {
  background-color: #1e293b/80;
}
:global(.dark) .ck-content :deep(table tbody td) {
  border-bottom-color: #1e293b;
  color: #cbd5e1;
}
:global(.dark) .ck-content :deep(table tbody td:first-child) {
  color: #34d399;
}

/* ── Responsive Tables ── */
@media (max-width: 640px) {
  .ck-content :deep(table) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* ── Scrollbars utility ── */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
